import { useState, useEffect } from 'react';
import { api } from '../api';
import bookingLinks from '../bookingLinks';

const SOURCE_NAMES = {
  hyatt: 'World of Hyatt',
  marriott: 'Marriott Bonvoy',
  hilton: 'Hilton Honors',
  ihg: 'IHG One Rewards',
};

// Map seats.aero source slug → bookingLinks key
const SOURCE_TO_BOOKING_KEY = {
  hyatt: 'World of Hyatt',
  marriott: 'Marriott Bonvoy',
  hilton: 'Hilton Honors',
};

function formatSource(s) {
  return SOURCE_NAMES[s] || s.charAt(0).toUpperCase() + s.slice(1);
}

export default function LiveHotels({ destinationCode, checkin, checkout }) {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!destinationCode) return;
    setLoading(true);
    setError(null);
    setResults(null);
    api.searchLiveHotels(destinationCode, checkin, checkout)
      .then(data => setResults(data))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [destinationCode, checkin, checkout]);

  if (loading) {
    return (
      <div className="bg-navy-light border border-brand-ink/10 rounded-xl p-6">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-teal animate-pulse" />
          <div>
            <div className="font-semibold text-sm">Searching live hotel award rates...</div>
            <div className="text-xs text-brand-ink/65">Checking Hyatt, Marriott, Hilton, IHG near {destinationCode}</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !results) return null;

  if (results.count === 0) {
    return (
      <div className="bg-navy-light border border-brand-ink/10 rounded-xl p-4">
        <div className="text-xs text-brand-ink/65">No live hotel award rates cached for {destinationCode} on your dates yet. Use the hotel options below or search the loyalty sites directly.</div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-indigo-500/5 to-navy-light border border-indigo-500/20 rounded-xl overflow-hidden">
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          <span className="text-xs font-bold text-indigo-300 uppercase tracking-wider">Live Hotel Award Rates</span>
          <span className="text-[10px] text-brand-ink/55 ml-auto">via seats.aero</span>
        </div>
        <div className="text-sm text-brand-ink/75 mt-1">
          {results.count} result{results.count !== 1 ? 's' : ''} across {results.sources.length} program{results.sources.length !== 1 ? 's' : ''}
        </div>
      </div>

      <div className="px-4 pb-4 space-y-2">
        {results.sources.map(source => {
          const items = results.bySource[source] || [];
          const available = items.filter(r => r.available);
          if (available.length === 0) return null;

          const link = bookingLinks[SOURCE_TO_BOOKING_KEY[source]];
          return (
            <div key={source} className="bg-brand-ink/5 rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm">{formatSource(source)}</span>
                <span className="text-xs text-brand-ink/65">{available.length} night{available.length !== 1 ? 's' : ''} available</span>
              </div>
              {available.slice(0, 5).map((r, i) => (
                <div key={r.id || i} className="flex items-center justify-between text-xs border-t border-brand-ink/5 pt-1.5">
                  <div>
                    <span className="text-brand-ink/75">{new Date(r.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                    {r.remainingRooms > 0 && (
                      <span className="text-teal ml-2">({r.remainingRooms} room{r.remainingRooms !== 1 ? 's' : ''})</span>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-sm">{r.pointsCost}</span>
                    <span className="text-brand-ink/65 ml-1">pts/night</span>
                    {r.taxes > 0 && <span className="text-brand-ink/55 ml-1">+ {r.taxesCurrency === 'USD' ? '$' : ''}{r.taxes}</span>}
                  </div>
                </div>
              ))}
              {available.length > 5 && (
                <div className="text-[10px] text-brand-ink/55 text-center">+ {available.length - 5} more dates</div>
              )}
              {link && (
                <a
                  href={link.search}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-center py-1.5 mt-1 rounded-md text-xs font-semibold bg-indigo-600/80 text-brand-cream hover:bg-indigo-600 transition"
                >
                  Book on {formatSource(source)} ↗
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
